import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {Menu, X, Pickaxe, ChevronDown, Bell} from 'lucide-react';
import { useAuth } from "@/hooks/useAuth";

const minerals = [
  { name: "Gold", color: "#f59e0b", path: "/jobs/gold" },
  { name: "Copper", color: "#b45309", path: "/jobs/copper" },
  { name: "Coal", color: "#6b7280", path: "/jobs/coal" },
  { name: "Iron Ore", color: "#dc2626", path: "/jobs/iron-ore" },
];

export default function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mineralOpen, setMineralOpen] = useState(false);
  const location = useLocation();
  const { isAuthenticated, isAdmin } = useAuth();

  const navLinks = [
    { label: "Browse Jobs", path: "/" },
    { label: "Entry Level", path: "/entry-level" },
    { label: "FIFO Jobs", path: "/fifo" },
    { label: "ROI Calculator", path: "/calculator" },
    { label: "SEO Dashboard", path: "/seo" },
  ];

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 border-b border-white/5" style={{ background: "rgba(17,24,39,0.95)", backdropFilter: "blur(12px)" }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 group">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: "linear-gradient(135deg, #f97316, #ea580c)" }}>
              <Pickaxe size={16} className="text-white" />
            </div>
            <span className="text-white font-bold text-lg" style={{ fontFamily: "Outfit, sans-serif" }}>
              Mineral<span style={{ color: "#f97316" }}>Jobs</span>
            </span>
            <span className="hidden sm:flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border badge-live" style={{ borderColor: "rgba(249,115,22,0.4)", color: "#fb923c", background: "rgba(249,115,22,0.08)", fontFamily: "JetBrains Mono, monospace" }}>
              <span className="w-1.5 h-1.5 rounded-full bg-orange-400 inline-block"></span>
              LIVE
            </span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`nav-link px-3 py-2 text-sm rounded-lg transition-colors ${
                  location.pathname === link.path
                    ? "text-orange-400 bg-orange-500/10"
                    : "text-gray-300 hover:text-white hover:bg-white/5"
                }`}
              >
                {link.label}
              </Link>
            ))}

            {/* Jobs by Mineral dropdown */}
            <div className="relative">
              <button
                onClick={() => setMineralOpen(!mineralOpen)}
                className="flex items-center gap-1 px-3 py-2 text-sm rounded-lg text-gray-300 hover:text-white hover:bg-white/5 transition-colors"
              >
                By Mineral <ChevronDown size={14} className={`transition-transform ${mineralOpen ? "rotate-180" : ""}`} />
              </button>
              {mineralOpen ? (
                <div className="absolute top-full right-0 mt-2 w-44 rounded-xl border border-white/10 py-1 z-50" style={{ background: "#1a1f2e" }}>
                  {minerals.map((m) => (
                    <Link
                      key={m.name}
                      to={m.path}
                      onClick={() => setMineralOpen(false)}
                      className="flex items-center gap-2 px-4 py-2 text-sm text-gray-300 hover:text-white hover:bg-white/5 transition-colors"
                    >
                      <span className="w-2 h-2 rounded-full" style={{ background: m.color }}></span>
                      {m.name}
                    </Link>
                  ))}
                </div>
              ) : null}
            </div>
          </div>

          {/* CTA + Mobile Toggle */}
          <div className="flex items-center gap-3">
            <Link
              to={isAuthenticated ? "/alerts" : "/auth?redirect=/alerts&mode=register"}
              className="hidden sm:inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm text-gray-300 hover:text-white hover:bg-white/5 transition-colors"
            >
              <Bell size={14} /> Alerts
            </Link>
            {isAdmin ? (
              <Link
                to="/admin"
                className="hidden sm:inline-flex px-3 py-2 rounded-lg text-sm text-orange-400 hover:bg-orange-500/10 transition-colors"
              >
                Admin
              </Link>
            ) : null}
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="md:hidden text-gray-400 hover:text-white p-1"
            >
              {mobileOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileOpen ? (
        <div className="md:hidden border-t border-white/5 px-4 py-4 space-y-1" style={{ background: "#111827" }}>
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              onClick={() => setMobileOpen(false)}
              className="block px-4 py-2.5 text-sm rounded-lg text-gray-300 hover:text-white hover:bg-white/5 transition-colors"
            >
              {link.label}
            </Link>
          ))}
          <div className="pt-2 border-t border-white/5">
            <p className="px-4 py-1 text-xs text-gray-500 uppercase tracking-wider" style={{ fontFamily: "JetBrains Mono, monospace" }}>Jobs by Mineral</p>
            {minerals.map((m) => (
              <Link
                key={m.name}
                to={m.path}
                onClick={() => setMobileOpen(false)}
                className="flex items-center gap-2 px-4 py-2 text-sm text-gray-300 hover:text-white"
              >
                <span className="w-2 h-2 rounded-full" style={{ background: m.color }}></span>
                {m.name}
              </Link>
            ))}
          </div>
          <Link
            to={isAuthenticated ? "/alerts" : "/auth?redirect=/alerts&mode=register"}
            onClick={() => setMobileOpen(false)}
            className="block px-4 py-2.5 text-sm rounded-lg text-gray-300 hover:text-white hover:bg-white/5"
          >
            Job Alerts
          </Link>
          {isAdmin ? (
            <Link
              to="/admin"
              onClick={() => setMobileOpen(false)}
              className="block px-4 py-2.5 text-sm rounded-lg text-orange-400 hover:bg-orange-500/10"
            >
              Admin
            </Link>
          ) : null}
        </div>
      ) : null}
    </nav>
  );
}
